import { z } from 'zod';
import { TrainerLocationKindSchema, type TrainerLocationKind } from './trainer-profile.js';

/**
 * F33 — trainer availability read contract. F33 defines this; F34 implements the
 * real DB-backed calendar editor (the F20→F21 PaymentConfigProvider precedent).
 * F33 ships `StubTrainerAvailabilityProvider`, so the book-trainer flow has a
 * slot grid before F34.
 */

export const GetTrainerAvailabilityArgsSchema = z
  .object({
    trainerId: z.string().uuid(),
    /** Inclusive ISO date (YYYY-MM-DD), venue/trainer local. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    locationKind: TrainerLocationKindSchema.optional(),
    /** Required to intersect with court availability for affiliated_venue mode. */
    venueId: z.string().uuid().optional(),
    subVenueId: z.string().uuid().optional(),
    /**
     * Slot length; defaults to the package's session_duration_minutes. Coerced:
     * this schema parses the `GET …/availability` QUERY STRING, where every
     * value arrives as a string (`?slotMinutes=60`) — a bare `z.number()` 422'd
     * every client that sent it (mobile always does).
     */
    slotMinutes: z.coerce.number().int().min(15).max(240).optional(),
  })
  .strict();
export type GetTrainerAvailabilityArgs = z.infer<typeof GetTrainerAvailabilityArgsSchema>;

export type TrainerAvailabilitySlot = {
  readonly startAt: string;
  readonly endAt: string;
  readonly available: boolean;
};

export type TrainerAvailabilityDay = {
  /** ISO date (YYYY-MM-DD). */
  readonly date: string;
  readonly slots: readonly TrainerAvailabilitySlot[];
};

export type TrainerAvailabilityResult = {
  readonly days: readonly TrainerAvailabilityDay[];
  /** `'stub'` until F34 registers `DefaultTrainerAvailabilityProvider`. */
  readonly source: 'stub' | 'trainer_calendar';
  readonly locationKind: TrainerLocationKind | null;
};

/* ─── F34: availability WRITE contracts (recurring rules + one-off blocks + settings) ─── */

export const TRAINER_AVAILABILITY_SCOPE_VALUES = ['everywhere', 'venue'] as const;
export type TrainerAvailabilityScope = (typeof TRAINER_AVAILABILITY_SCOPE_VALUES)[number];
export const TrainerAvailabilityScopeSchema = z.enum(TRAINER_AVAILABILITY_SCOPE_VALUES);

/** A recurring weekly availability rule. D4: `appliesTo='venue'` scopes it to one venue. */
export type TrainerAvailabilityRule = {
  readonly id: string;
  readonly trainerUserId: string;
  readonly appliesTo: TrainerAvailabilityScope;
  readonly venueId: string | null;
  /** 0 = Sunday … 6 = Saturday. */
  readonly dayOfWeek: number;
  /** Minutes from midnight in the trainer's timezone, 0–1439. */
  readonly startMinute: number;
  /** Minutes from midnight, 1–1440 (must be > startMinute). */
  readonly endMinute: number;
  readonly isActive: boolean;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export const TrainerAvailabilityRuleInputSchema = z
  .object({
    appliesTo: TrainerAvailabilityScopeSchema.default('everywhere'),
    venueId: z.string().uuid().nullable().optional(),
    dayOfWeek: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    isActive: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.endMinute > v.startMinute, {
    message: 'endMinute must be after startMinute',
    path: ['endMinute'],
  })
  .refine((v) => (v.appliesTo === 'venue') === (v.venueId != null), {
    message: "venueId must be set if and only if appliesTo is 'venue'",
    path: ['venueId'],
  });
export type TrainerAvailabilityRuleInput = z.infer<typeof TrainerAvailabilityRuleInputSchema>;

/** PUT replaces the whole rule set (stateless editor — the F12 pricing precedent). */
export const ReplaceTrainerAvailabilityRulesInputSchema = z
  .object({ rules: z.array(TrainerAvailabilityRuleInputSchema).max(200) })
  .strict();
export type ReplaceTrainerAvailabilityRulesInput = z.infer<
  typeof ReplaceTrainerAvailabilityRulesInputSchema
>;

export const TRAINER_AVAILABILITY_BLOCK_KIND_VALUES = ['block', 'holiday'] as const;
export type TrainerAvailabilityBlockKind = (typeof TRAINER_AVAILABILITY_BLOCK_KIND_VALUES)[number];
export const TrainerAvailabilityBlockKindSchema = z.enum(TRAINER_AVAILABILITY_BLOCK_KIND_VALUES);

/** A one-off block / holiday. `venueId` NULL = blocks everywhere. */
export type TrainerAvailabilityBlock = {
  readonly id: string;
  readonly trainerUserId: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly reason: string | null;
  readonly venueId: string | null;
  readonly kind: TrainerAvailabilityBlockKind;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export const CreateTrainerAvailabilityBlockInputSchema = z
  .object({
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    reason: z.string().max(200).nullable().optional(),
    venueId: z.string().uuid().nullable().optional(),
    kind: TrainerAvailabilityBlockKindSchema.default('block'),
  })
  .strict()
  .refine((v) => new Date(v.endAt) > new Date(v.startAt), {
    message: 'endAt must be after startAt',
    path: ['endAt'],
  });
export type CreateTrainerAvailabilityBlockInput = z.infer<
  typeof CreateTrainerAvailabilityBlockInputSchema
>;

/** Per-trainer global availability config + vacation mode. Auto-created on first read. */
export type TrainerAvailabilitySettings = {
  readonly trainerUserId: string;
  readonly defaultSlotMinutes: number;
  readonly defaultBufferMinutes: number;
  readonly minLeadMinutes: number;
  readonly maxAdvanceDays: number;
  readonly vacationModeActive: boolean;
  readonly vacationModeUntil: string | null;
  readonly timezone: string;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export const UpdateTrainerAvailabilitySettingsInputSchema = z
  .object({
    defaultSlotMinutes: z.number().int().min(15).max(240),
    defaultBufferMinutes: z.number().int().min(0).max(240),
    minLeadMinutes: z.number().int().min(0),
    maxAdvanceDays: z.number().int().min(1).max(365),
    vacationModeActive: z.boolean(),
    vacationModeUntil: z.string().datetime({ offset: true }).nullable(),
    timezone: z.string().min(1).max(64),
  })
  .partial()
  .strict();
export type UpdateTrainerAvailabilitySettingsInput = z.infer<
  typeof UpdateTrainerAvailabilitySettingsInputSchema
>;
